<template>
  <div :class="['dn-group', skin?.toLowerCase()]">
    <div
      v-if="logo || header || (readmoreTxt && readmoreUrl)"
      class="dn-group-header"
    >
      <dn-icon
        v-if="logo"
        :name="logo"
        :class="['logo', logo?.toLowerCase()]"
      />
      <dn-headline
        v-else-if="header"
        tag="h3"
        class="header"
      >
        <dn-icon
          v-if="breakingHeader"
          name="pulsating"
        />
        {{ header }}
      </dn-headline>
      <dn-link
        v-if="readmoreTxt && readmoreUrl"
        :href="readmoreUrl"
        class="readmore"
        external
      >
        <span>{{ readmoreTxt }}</span>
        <dn-icon
          name="right-arrow-light"
          class="arrow"
        />
      </dn-link>
    </div>
    <dn-component
      v-for="(item, index) in items"
      :key="index + item.type"
      v-bind="{ ...item, theme_layout: props.skin }"
    />
  </div>
</template>
<script setup lang="ts">
const props = defineProps({
  skin: {
    type: String,
    required: false,
    default: null
  },
  breakingHeader: {
    type: Boolean,
    required: false,
    default: false
  },
  header: {
    type: String,
    required: false,
    default: null
  },
  logo: {
    type: String,
    required: false,
    default: null
  },
  readmoreTxt: {
    type: String,
    required: false,
    default: null
  },
  readmoreUrl: {
    type: String,
    required: false,
    default: null
  },
  layout: {
    type: String,
    required: false,
    default: null
  },
  items: {
    type: Array,
    required: false,
    default: () => []
  }
});
</script>

<style lang="scss">
.dn-group {
  --_dn-group_background: var(--dn-group_background);
  --_dn-group_border-radius: var(--dn-group_border-radius, 4px);
  --_dn-group_spacing: var(--dn-group_spacing, 0);
  --_dn-group_grid-spacing: var(--dn-group_grid-spacing, 12px);
  --_dn-group-header_spacing: var(--dn-group-header_spacing, 12px);

  background-color: var(--_dn-group_background);
  border-radius: var(--_dn-group_border-radius);
  padding: var(--_dn-group_spacing);

  &-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding-bottom: var(--_dn-group-header_spacing);

    .header {
      --dn-headline_font-family: var(--dn-group--header_font-family);
      --dn-headline_font-weight: var(--dn-group--header_font-weight, 500);
      --dn-headline_font-size: var(--dn-group--header_font-size, 20px);
      --dn-headline_line-height: var(--dn-group--header_line-height, 100%);
      --dn-headline_spacing: var(--dn-group--header_spacing, 0);

      display: flex;
      align-items: center;
      gap: 8px;

      .dn-icon {
        --dn-icon_color: var(--dn-group-icon_color);

        width: 30px;
        height: 30px;
      }
    }

    .logo {
      --dn-icon_color: var(--dn-group--logo_color, #232528);

      @media only screen and (width < 980px) {
        &.d2 {
          height: 36px;
        }

        &.smak {
          height: 28px;
        }

        &.dnhelg {
          height: 36px;
        }
      }
    }

    .dn-link {
      &.readmore {
        --dn-link_font-size: var(--dn-group--readmore_font-size, 16px);
        --dn-link_color: var(--dn-group--readmore_color, #232528);
        --dn-link_color__hover: var(--dn-group--readmore_color__hover, #232528);
        --dn-link_color__active: var(--dn-group--readmore_color__active, #232528);
        --dn-icon_color: var(--dn-group--readmore-icon_color, #232528);

        display: flex;
        align-items: center;
        gap: 10px;
      }
    }
  }

  > .dn-grid {
    &:not(:last-child) {
      padding-bottom: var(--_dn-group_grid-spacing);
    }
  }

  &.standard {
    --_dn-group_spacing: var(--dn-group_spacing, 0);
  }

  &.brand {
    --dn-group_background: var(--color-teaser-brand-bg);
    --dn-group-icon_color: var(--color-teaser-highlight-bg);
    --dn-headline_color: var(--color-teaser-brand-fg);
    --dn-group--logo_color: var(--color-teaser-brand-fg);
    --dn-group--readmore_color: var(--color-teaser-brand-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-brand-fg);
    --dn-group--readmore_color__active: var(--color-teaser-brand-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-brand-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-brand-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-brand-divider);
      --dn-teaser-card--link-color: var(--color-teaser-brand-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-brand-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-brand-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-brand-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-brand-fg);
      --dn-teaser-card--kicker-icon_color: var(--color-teaser-brand-fg);
    }
  }

  &.brand-secondary {
    --dn-group_background: var(--color-teaser-brand-secondary-bg);
    --dn-group-icon_color: var(--color-teaser-brand-secondary-fg);
    --dn-headline_color: var(--color-teaser-brand-secondary-fg);
    --dn-group--logo_color: var(--color-teaser-brand-secondary-fg);
    --dn-group--readmore_color: var(--color-teaser-brand-secondary-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-brand-secondary-fg);
    --dn-group--readmore_color__active: var(--color-teaser-brand-secondary-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-brand-secondary-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-brand-secondary-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-brand-secondary-divider);
      --dn-teaser-card--link-color: var(--color-teaser-brand-secondary-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-brand-secondary-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-brand-secondary-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-brand-secondary-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-brand-secondary-fg);
    }
  }

  &.feature {
    --dn-group_background: var(--color-teaser-feature-bg);
    --dn-group-icon_color: var(--color-teaser-feature-fg);
    --dn-headline_color: var(--color-teaser-feature-fg);
    --dn-group--logo_color: var(--color-teaser-feature-fg);
    --dn-group--readmore_color: var(--color-teaser-feature-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-feature-fg);
    --dn-group--readmore_color__active: var(--color-teaser-feature-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-feature-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-feature-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-feature-divider);
      --dn-teaser-card--link-color: var(--color-teaser-feature-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-feature-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-feature-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-feature-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-brand-feature-fg);
    }
  }

  &.highlight {
    --dn-group_background: var(--color-teaser-highlight-bg);
    --dn-group-icon_color: var(--color-teaser-highlight-fg);
    --dn-headline_color: var(--color-teaser-highlight-fg);
    --dn-group--logo_color: var(--color-teaser-highlight-fg);
    --dn-group--readmore_color: var(--color-teaser-highlight-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-highlight-fg);
    --dn-group--readmore_color__active: var(--color-teaser-highlight-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-highlight-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-highlight-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-highlight-divider);
      --dn-teaser-card--link-color: var(--color-teaser-highlight-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-highlight-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-highlight-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-highlight-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-highlight-fg);
      --dn-teaser-card--kicker-icon_color: var(--color-teaser-highlight-fg);
    }
  }

  &.inverted {
    --dn-group_background: var(--color-teaser-inverted-bg);
    --dn-group-icon_color: var(--color-teaser-inverted-highlight-fg);
    --dn-headline_color: var(--color-teaser-inverted-fg);
    --dn-group--logo_color: var(--color-teaser-inverted-fg);
    --dn-group--readmore_color: var(--color-teaser-inverted-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-inverted-fg);
    --dn-group--readmore_color__active: var(--color-teaser-inverted-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-inverted-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-inverted-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-inverted-divider);
      --dn-teaser-card--link-color: var(--color-teaser-inverted-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-inverted-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-inverted-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-inverted-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-inverted-fg);
      --dn-teaser-card--kicker-icon_color: var(--color-teaser-inverted-fg);
    }
  }

  &.opinion {
    --dn-group_background: var(--color-teaser-opinion-bg);
    --dn-group-icon_color: var(--color-teaser-opinion-fg);
    --dn-headline_color: var(--color-teaser-opinion-fg);
    --dn-group--logo_color: var(--color-teaser-opinion-fg);
    --dn-group--readmore_color: var(--color-teaser-opinion-fg);
    --dn-group--readmore_color__hover: var(--color-teaser-opinion-fg);
    --dn-group--readmore_color__active: var(--color-teaser-opinion-fg);
    --dn-group--readmore-icon_color: var(--color-teaser-opinion-fg);

    .dn-teaser-card {
      --dn-teaser-card--card_background: var(--color-teaser-opinion-bg);
      --dn-teaser-card--card_border-bottom-color: var(--color-teaser-opinion-divider);
      --dn-teaser-card--link-color: var(--color-teaser-opinion-fg);
      --dn-teaser-card--link-color__hover: var(--color-teaser-opinion-fg);
      --dn-teaser-card--link-color__active: var(--color-teaser-opinion-fg);
      --dn-teaser-card--meta_color: var(--color-teaser-opinion-subtle-fg);
      --dn-teaser-card--kicker_color: var(--color-teaser-opinion-fg);
      --dn-teaser-card--kicker-icon_color: var(--color-teaser-opinion-fg);
      --dn-teaser-card--title_font-family: var(--typography-teaser-opinion-a-large-max-font-family);

      :not(.opinion) {
        --dn-teaser-card--title_font-family: var(--typography-teaser-opinion-a-large-max-font-family);

        .title {
          &:has(strong) {
            --dn-teaser-card--title_font-family: var(--typography-teaser-opinion-a-large-max-font-family);
          }
        }
      }
    }
  }

  &.brand,
  &.highlight,
  &.inverted,
  &.opinion,
  &.brand-secondary,
  &.feature {
    --dn-collection-content-padding: var(--site-padding);
    --dn-live-wrapped-ad_padding-multiply: 4;
    --dn-job-carousel-desktop_padding-multiply: 4;
    --dn-job-carousel-mobile_padding-multiply: 6;

    .layout-ab,
    .layout-ba,
    .layout-bb,
    .layout-aa,
    .layout-a\+,
    .layout-a {
      > .dn-teaser-card {
        --dn-teaser-card--card_border-bottom-width: 0;
        --dn-teaser-card--card_padding: 0;
      }

      --dn-skeleton_lines_border-bottom-width: 0;
      --dn-skeleton_lines_padding: 0;
    }

    .layout-abb {
      &:last-child {
        > :nth-child(1) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        @media only screen and (width < 980px) {
          > :nth-child(2) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(3) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }
    }

    .layout-bba {
      &:last-child {
        @media only screen and (width < 980px) {
          > :nth-child(1) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }

        > :nth-child(2) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }

        > :nth-child(3) {
          @media only screen and (width >= 980px) {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-skeleton_lines_border-bottom-width: 0;
            --dn-skeleton_lines_padding: 0;
          }
        }
      }
    }

    .layout-bbb,
    .layout-bab,
    .layout-bbbb {
      > .dn-teaser-card {
        @media only screen and (width >= 980px) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
        }

        &:nth-last-child(1) {
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
        }
      }

      @media only screen and (width >= 980px) {
        --dn-skeleton_lines_border-bottom-width: 0;
        --dn-skeleton_lines_padding: 0;
      }

      > :nth-last-child(1) {
        --dn-skeleton_lines_border-bottom-width: 0;
        --dn-skeleton_lines_padding: 0;
      }
    }

    .layout-a,
    .layout-b {
      &:only-child {
        > .dn-teaser-card {
          --dn-teaser-card--card_border-radius: var(--border-radius-teaser-max);
          --dn-teaser-card--card_image_border-radius: var(--box-radius) 0 0 var(--box-radius);
          --dn-teaser-card--card_border-bottom-width: 0;
          --dn-teaser-card--card_padding: 0;
          --dn-teaser-card--assets_spacing: 0;
          --dn-teaser-card--title_spacing: var(--row-gap) var(--row-gap) 0 0;
          --dn-teaser-card--lead_spacing: 0 var(--row-gap) 0 0;
          --dn-teaser-card--meta_spacing: 0 var(--row-gap) var(--row-gap) 0;
          --dn-teaser-card--kicker_spacing: var(--row-gap) var(--row-gap) 0 0;
          --dn-teaser-card--strapline_spacing: 0 var(--row-gap) 0 0;
          --dn-teaser-card--card_pseudo_border-bottom-width: 0;
        }

        --dn-skeleton_lines_border-bottom-width: 0;
        --dn-skeleton_lines_padding: 0;
      }

      &:last-child {
        &:not(:only-child) {
          > .dn-teaser-card {
            --dn-teaser-card--card_border-bottom-width: 0;
            --dn-teaser-card--card_padding: 0;
            --dn-teaser-card--meta_spacing: 0;
            --dn-teaser-card--card_pseudo_border-bottom-width: 0;
          }

          --dn-skeleton_lines_border-bottom-width: 0;
          --dn-skeleton_lines_padding: 0;
        }
      }
    }

    @media only screen and (width < 980px) {
      .layout-a,
      .layout-b:has(.dn-image-mobile-top) {
        &:only-child {
          > .dn-teaser-card {
            --dn-teaser-card--card_image_border-radius: var(--box-radius) var(--box-radius) 0 0;
            --dn-teaser-card--card_padding: 0 0 var(--teaser-spacing);
            --dn-teaser-card--assets_spacing: 0;
            --dn-teaser-card--title_spacing: 0 var(--row-gap);
            --dn-teaser-card--lead_spacing: 0 var(--row-gap);
            --dn-teaser-card--meta_spacing: 0 var(--row-gap);
            --dn-teaser-card--kicker_spacing: 0 var(--row-gap);
          }
        }
      }
    }
  }
}
</style>
